import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/ascend/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Bookmark, BookmarkX, ExternalLink, Share2, ArrowLeft, Newspaper } from "lucide-react";
import { toast, Toaster } from "sonner";

function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return "";
  const A = 0x1f1e6;
  const c0 = cc.toUpperCase().charCodeAt(0) - 65;
  const c1 = cc.toUpperCase().charCodeAt(1) - 65;
  if (c0 < 0 || c1 < 0 || c0 > 25 || c1 > 25) return "";
  return String.fromCodePoint(A + c0) + String.fromCodePoint(A + c1);
}
function formatRelative(iso) {
  if (!iso) return "recently";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "recently";
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const d = Math.floor(diff / 86400);
  if (d === 1) return "yesterday";
  if (d < 7) return `${d} days ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function NewsSaved() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/news/saved`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (hash) => {
    const prev = items;
    setItems((xs) => xs.filter((x) => x.article_hash !== hash));
    try {
      await api.delete(`/news/saved/${hash}`);
      toast.success("Removed from Read Weekend");
    } catch (e) {
      setItems(prev);
      toast.error(formatApiError(e));
    }
  };

  const share = async (item) => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: item.title, url: item.article_link });
        return;
      } catch (_) { /* user cancelled or unsupported */ }
    }
    try {
      await navigator.clipboard.writeText(item.article_link);
      toast.success("Link copied");
    } catch (_) {
      toast.error("Copy failed — long-press the link to copy.");
    }
  };

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="fade-up space-y-8" data-testid="news-saved-page">
        <header>
          <Link to="/news" className="inline-flex items-center gap-1 text-sm text-muted hover:opacity-80" data-testid="news-saved-back">
            <ArrowLeft size={14} strokeWidth={1.5} />
            Back to News
          </Link>
          <div className="flex items-center gap-2 mt-3 text-xs uppercase tracking-widest text-muted">
            <Bookmark size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
            Read Weekend
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>
            Your saved reading
          </h1>
          <p className="font-hand text-lg mt-2" style={{ color: "var(--accent)" }}>
            slow-cooked for a Saturday morning
          </p>
        </header>

        {loading ? (
          <div className="text-muted text-sm" data-testid="news-saved-loading">a moment…</div>
        ) : items.length === 0 ? (
          <EmptySaved />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="news-saved-grid">
            {items.map((it) => (
              <SavedCard key={it.article_hash} item={it} onRemove={() => remove(it.article_hash)} onShare={() => share(it)} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function SavedCard({ item, onRemove, onShare }) {
  return (
    <div className="card-soft p-0 overflow-hidden flex flex-col relative" data-testid="news-saved-card">
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
        <button
          type="button"
          onClick={onShare}
          className="p-2 rounded-full backdrop-blur-sm transition-transform active:scale-90"
          style={{
            background: "color-mix(in oklab, var(--surface) 82%, transparent)",
            color: "var(--text-muted)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
          aria-label="Share this article"
          data-testid="news-saved-share"
        >
          <Share2 size={15} strokeWidth={1.75} />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="p-2 rounded-full backdrop-blur-sm transition-transform active:scale-90"
          style={{
            background: "color-mix(in oklab, var(--surface) 82%, transparent)",
            color: "var(--primary)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
          aria-label="Remove from Read Weekend"
          data-testid="news-saved-remove"
        >
          <BookmarkX size={15} strokeWidth={1.75} />
        </button>
      </div>

      {item.image_url && (
        <div className="w-full aspect-[16/9] overflow-hidden" style={{ background: "var(--surface-2)" }}>
          <img
            src={item.image_url}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          {item.source_name && (
            <span
              className="px-2 py-0.5 rounded-full"
              style={{
                background: "color-mix(in oklab, var(--primary) 10%, transparent)",
                color: "var(--primary)",
              }}
            >
              {item.source_name}
            </span>
          )}
          <span className="text-muted">· saved {formatRelative(item.saved_at)}</span>
        </div>
        {item.source_country_code && (
          <span
            className="mt-2 self-start inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full"
            style={{
              background: "var(--surface-2)",
              color: "var(--text-muted)",
            }}
          >
            <span aria-hidden="true">{flagEmoji(item.source_country_code)}</span>
            <span>{item.source_country_code}</span>
          </span>
        )}
        <h3 className="font-display text-lg leading-snug mt-3" style={{ color: "var(--text)" }}>{item.title}</h3>
        {item.summary && (
          <p className="text-sm mt-2 text-muted" style={{ display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {item.summary}
          </p>
        )}
        <a
          href={item.article_link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium"
          style={{ color: "var(--primary)" }}
          data-testid="news-saved-read-link"
        >
          Read article
          <ExternalLink size={12} strokeWidth={1.75} />
        </a>
      </div>
    </div>
  );
}

function EmptySaved() {
  return (
    <div className="card-soft p-10 text-center max-w-xl mx-auto" data-testid="news-saved-empty">
      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
           style={{ background: "color-mix(in oklab, var(--accent) 20%, var(--surface-2))" }}>
        <Newspaper size={22} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
      </div>
      <div className="font-display text-2xl mb-2" style={{ color: "var(--text)" }}>Nothing saved yet</div>
      <p className="text-sm text-muted mb-5">Bookmark articles on the News page to build your weekend reading.</p>
      <Link to="/news" className="btn-primary inline-flex items-center gap-1.5 text-sm">
        Browse the News page
        <ExternalLink size={12} strokeWidth={1.75} />
      </Link>
    </div>
  );
}
